import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { theme } from '../../../theme';
import { formatDateTime } from '../hooks/useRelativeTime';
import { addIncidentNote } from '../services/assessmentHistoryService';
import type { IncidentNote } from '../types/assessmentHistory.types';

type NotesSectionProps = {
  incidentId: string;
  notes: IncidentNote[];
  currentUserId: string;
  onNoteAdded: (note: IncidentNote) => void;
};

export const NotesSection = ({ incidentId, notes, currentUserId, onNoteAdded }: NotesSectionProps) => {
  const [noteText, setNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!noteText.trim() || isSaving) return;

    setIsSaving(true);
    try {
      const newNote = await addIncidentNote(incidentId, noteText.trim(), currentUserId);
      onNoteAdded(newNote);
      setNoteText('');
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      {/* Section header */}
      <div className={`flex items-center gap-2 mb-3 pb-2 border-b ${theme.border.base}`}>
        <MessageSquare className={`w-3.5 h-3.5 ${theme.text.muted}`} />
        <h4 className={`text-xs font-bold uppercase tracking-widest ${theme.text.muted}`}>
          Notes ({notes.length})
        </h4>
      </div>

      {/* Existing notes */}
      {notes.length === 0 ? (
        <p className={`text-xs ${theme.text.muted} mb-4`}>No notes yet.</p>
      ) : (
        <div className="space-y-2.5 mb-4">
          {notes.map((note) => (
            <div key={note.id} className={theme.card.paddedMuted}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[11px] font-semibold ${theme.text.subheading}`}>
                  {note.author_name ?? 'Team member'}
                </span>
                <span className={`text-[10px] ${theme.text.muted}`}>
                  {formatDateTime(note.created_at)}
                </span>
              </div>
              <p className={`text-sm ${theme.text.body} leading-relaxed`}>
                {note.note_text}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Add note input */}
      <textarea
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        placeholder="Add a follow-up note..."
        rows={3}
        className={theme.input.textarea}
      />
      <button
        onClick={handleSave}
        disabled={!noteText.trim() || isSaving}
        className={`mt-2 ${theme.button.medBase} ${theme.button.medTeal} text-sm disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        {isSaving ? 'Saving...' : 'Save Note'}
      </button>
    </div>
  );
};
